import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import JobBilling from '../components/JobBilling';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const toLocalInput = (ts) => (ts ? new Date(ts).toISOString().slice(0, 16) : '');
const num = (x) => (x === '' || x == null ? '' : x);

const emptyFlight = () => ({
  fds_number: '', flight_date: today(), aircraft: '', aircraft_type: '', pilot: '', sic: '', departure: '', arrival: '',
  depart_at: '', arrive_at: '', hobbs_out: '', hobbs_in: '', tach_out: '', tach_in: '', purpose: '', notes: '',
});
const emptyForm = () => ({ customer_id: '', title: '', status: 'open', flight: emptyFlight() });

const hobbsTime = (f) => {
  const a = parseFloat(f.hobbs_out), b = parseFloat(f.hobbs_in);
  return Number.isFinite(a) && Number.isFinite(b) && b >= a ? (b - a).toFixed(1) : null;
};

export default function FlightJobs() {
  const { activeId, activeBusiness } = useBusiness();
  const { can, user } = useAuth();
  const editable = can('jobs.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [openRowInfo, setOpenRowInfo] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api(`/jobs?business_id=${activeId}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => { setRows([]); if (e?.status && e.status !== 404) setError('Could not load flights.'); })
      .finally(() => setLoading(false));
  }, [activeId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api(`/customers?business_id=${activeId}`).then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
  }, [activeId]);

  function openNew() { setOpenRowInfo(null); setCurrent(null); setForm(emptyForm()); setFormError(null); setOpen(true); }
  function syncForm(j) {
    const fl = j.flight || {};
    setForm({
      customer_id: j.customer_id || '', title: j.title || '', status: j.status || 'open',
      flight: {
        fds_number: fl.fds_number || '',
        flight_date: fl.flight_date?.slice(0, 10) || '', aircraft: fl.aircraft || '', aircraft_type: fl.aircraft_type || '',
        pilot: fl.pilot || '', sic: fl.sic || '', departure: fl.departure || '', arrival: fl.arrival || '',
        depart_at: toLocalInput(fl.depart_at), arrive_at: toLocalInput(fl.arrive_at),
        hobbs_out: num(fl.hobbs_out), hobbs_in: num(fl.hobbs_in), tach_out: num(fl.tach_out), tach_in: num(fl.tach_in),
        purpose: fl.purpose || '', notes: fl.notes || '',
      },
    });
  }
  async function openRow(row) {
    setFormError(null); setCurrent(null); setForm(emptyForm()); setOpenRowInfo(row); setOpen(true);
    try { const j = await api(`/jobs/${row.id}`); setCurrent(j); syncForm(j); }
    catch (e) { setFormError(e?.detail || 'Could not load this flight.'); }
  }
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFlight = (k, v) => setForm((f) => ({ ...f, flight: { ...f.flight, [k]: v } }));

  async function save() {
    if (!form.customer_id) { setFormError('Choose a customer.'); return; }
    setSaving(true); setFormError(null);
    const body = { business_id: activeId, type: 'flight', customer_id: Number(form.customer_id), title: form.title, status: form.status, flight: form.flight };
    try {
      if (current) await api(`/jobs/${current.id}`, { method: 'PUT', body });
      else await api('/jobs', { method: 'POST', body });
      setOpen(false); load();
    } catch (e) { setFormError(e?.detail || 'Could not save the flight.'); }
    finally { setSaving(false); }
  }

  const columns = [
    { key: 'job_number', header: 'Flight #', render: (r) => <span className="mono">{r.job_number}</span> },
    { key: 'fds_number', header: 'FDS #', render: (r) => r.fds_number || '—' },
    { key: 'flight_date', header: 'Date', render: (r) => (r.flight_date ? date(r.flight_date) : '—') },
    { key: 'aircraft', header: 'Aircraft', render: (r) => r.aircraft || '—' },
    { key: 'route', header: 'Route', render: (r) => (r.departure || r.arrival ? `${r.departure || '?'} → ${r.arrival || '?'}` : '—') },
    { key: 'pilot', header: 'Pilot', render: (r) => r.pilot || '—' },
    { key: 'customer_name', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const loadingExisting = openRowInfo && !current;
  async function deleteFlight() {
    if (!current) return;
    if (!window.confirm(`Delete flight ${current.job_number}? This permanently removes it and can’t be undone.`)) return;
    setSaving(true); setFormError(null);
    try { await api(`/jobs/${current.id}`, { method: 'DELETE' }); setOpen(false); load(); }
    catch (e) { setFormError(e?.detail || 'Could not delete the flight.'); }
    finally { setSaving(false); }
  }
  const footer = (
    <>
      <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Close</button>
      {current && user?.role === 'admin' && <button className="btn btn--danger" onClick={deleteFlight} disabled={saving}>Delete</button>}
      {editable && <button className="btn btn--primary" onClick={save} disabled={saving || loadingExisting}>{saving ? 'Saving…' : loadingExisting ? 'Loading…' : current ? 'Save changes' : 'Log flight'}</button>}
    </>
  );

  const fl = form.flight;
  const ht = hobbsTime(fl);

  return (
    <>
      <PageHeader
        title="Flights"
        actions={editable && <button className="btn btn--primary" onClick={openNew}>Log flight</button>}
      />
      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} error={error}
          getRowKey={(r) => r.id} onRowClick={openRow}
          empty={`No flights logged yet for ${activeBusiness?.name || 'this business'}.`} />
      </section>

      <Drawer open={open} title={current ? current.job_number : (openRowInfo ? openRowInfo.job_number : 'New flight')} width={720} onClose={() => !saving && setOpen(false)} footer={footer}>
        {formError && <div className="form-error">{formError}</div>}
        {current && <div className="inv-status-row"><StatusBadge status={current.status} /></div>}

        <div className="field"><label>Customer *</label>
          <select value={form.customer_id} disabled={!editable} onChange={(e) => setField('customer_id', e.target.value)}>
            <option value="">Select a customer…</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
          </select>
        </div>
        <div className="form-grid">
          <div className="field"><label>FDS number</label><input value={fl.fds_number} disabled={!editable} onChange={(e) => setFlight('fds_number', e.target.value)} placeholder="Flight Detail Sheet #" /></div>
          <div className="field"><label>Flight date</label><input type="date" value={fl.flight_date} disabled={!editable} onChange={(e) => setFlight('flight_date', e.target.value)} /></div>
          <div className="field"><label>Status</label>
            <select value={form.status} disabled={!editable} onChange={(e) => setField('status', e.target.value)}>
              <option value="open">Scheduled</option><option value="in_progress">In progress</option>
              <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <h3 className="form-section">Aircraft</h3>
        <div className="form-grid">
          <div className="field"><label>Aircraft (tail #)</label><input value={fl.aircraft} disabled={!editable} onChange={(e) => setFlight('aircraft', e.target.value)} placeholder="N12345" /></div>
          <div className="field"><label>Type</label><input value={fl.aircraft_type} disabled={!editable} onChange={(e) => setFlight('aircraft_type', e.target.value)} placeholder="e.g. C172" /></div>
          <div className="field"><label>Pilot in command</label><input value={fl.pilot} disabled={!editable} onChange={(e) => setFlight('pilot', e.target.value)} /></div>
          <div className="field"><label>Second in command (SIC)</label><input value={fl.sic} disabled={!editable} onChange={(e) => setFlight('sic', e.target.value)} /></div>
        </div>

        <h3 className="form-section">Route</h3>
        <div className="form-grid">
          <div className="field"><label>Departure</label><input value={fl.departure} disabled={!editable} onChange={(e) => setFlight('departure', e.target.value)} placeholder="e.g. KSMQ" /></div>
          <div className="field"><label>Arrival</label><input value={fl.arrival} disabled={!editable} onChange={(e) => setFlight('arrival', e.target.value)} placeholder="e.g. N51" /></div>
          <div className="field"><label>Depart at</label><input type="datetime-local" value={fl.depart_at} disabled={!editable} onChange={(e) => setFlight('depart_at', e.target.value)} /></div>
          <div className="field"><label>Arrive at</label><input type="datetime-local" value={fl.arrive_at} disabled={!editable} onChange={(e) => setFlight('arrive_at', e.target.value)} /></div>
        </div>

        <h3 className="form-section">Times {ht && <span className="hint">· Hobbs {ht}</span>}</h3>
        <div className="form-grid">
          <div className="field"><label>Hobbs out</label><input type="number" step="0.1" value={fl.hobbs_out} disabled={!editable} onChange={(e) => setFlight('hobbs_out', e.target.value)} /></div>
          <div className="field"><label>Hobbs in</label><input type="number" step="0.1" value={fl.hobbs_in} disabled={!editable} onChange={(e) => setFlight('hobbs_in', e.target.value)} /></div>
          <div className="field"><label>Tach out</label><input type="number" step="0.1" value={fl.tach_out} disabled={!editable} onChange={(e) => setFlight('tach_out', e.target.value)} /></div>
          <div className="field"><label>Tach in</label><input type="number" step="0.1" value={fl.tach_in} disabled={!editable} onChange={(e) => setFlight('tach_in', e.target.value)} /></div>
        </div>

        <div className="field"><label>Purpose</label><input value={fl.purpose} disabled={!editable} onChange={(e) => setFlight('purpose', e.target.value)} placeholder="e.g. ferry, training, charter" /></div>
        <div className="field"><label>Reference / title</label><input value={form.title} disabled={!editable} onChange={(e) => setField('title', e.target.value)} placeholder="Optional label" /></div>
        <div className="field"><label>Notes</label><textarea rows={3} value={fl.notes} disabled={!editable} onChange={(e) => setFlight('notes', e.target.value)} /></div>

        {current && <JobBilling job={current} />}
      </Drawer>
    </>
  );
}
